import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import styles from "./ModelsSidebar.module.css";
import { useThemedIcon } from "../../../hooks/useThemedIcon";

import parametricIconLight from "../../../assets/parametric_light.svg";
import parametricIconDark from "../../../assets/parametric_dark.svg";
import dockToLeftIconLight from "../../../assets/dock_to_left_light.svg";
import dockToLeftIconDark from "../../../assets/dock_to_left_dark.svg";
import ellipsisIconLight from "../../../assets/ellipsis_light.svg";
import ellipsisIconDark from "../../../assets/ellipsis_dark.svg";
import ellipsisActiveIconLight from "../../../assets/ellipsis_active_light.svg";
import ellipsisActiveIconDark from "../../../assets/ellipsis_active_dark.svg";
import renameIconLight from "../../../assets/rename_light.svg";
import renameIconDark from "../../../assets/rename_dark.svg";
import deleteIcon from "../../../assets/delete-red.svg";
import loadingIconLight from "../../../assets/loading_light.svg";
import loadingIconDark from "../../../assets/loading_dark.svg";
import searchIconLight from "../../../assets/search_light.svg";
import searchIconDark from "../../../assets/search_dark.svg";
import closeIconLight from "../../../assets/close_light.svg";
import closeIconDark from "../../../assets/close_dark.svg";
import chevronIconLight from "../../../assets/chevron_unit_light.svg";
import chevronIconDark from "../../../assets/chevron_unit_dark.svg";
import chevronActiveIconLight from "../../../assets/chevron_unit_active_light.svg";
import chevronActiveIconDark from "../../../assets/chevron_unit_active_dark.svg";

interface Project {
  id: string;
  name: string;
  createdAt: Date;
  updatedAt: Date;
  userId: string;
  thumbnail?: string;
  description?: string;
  isPublic?: boolean;
  shareToken?: string;
  copiedFromExample?: string;
}

interface ModelsSidebarProps {
  projects: Project[];
  onProjectSelect: (projectId: string) => void;
  onExampleSelect: (exampleId: string) => void;
  onProjectDelete: (projectId: string) => void;
  onProjectRename: (projectId: string, newName: string) => void;
  onClose: () => void;
  isMobile?: boolean;
  currentProjectId?: string | null;
  loadingProjectId?: string | null;
  openTabIds?: string[];
}

// Define the example project IDs (update these with your actual Firebase IDs)
const EXAMPLE_PROJECT_IDS = [
  "S79Nx7xikgyBxC78ksSf",
  "VkQgP0JkSWZmM1jIjeJN",
  "LHIiWYRVduGrYUe6hAyb",
];

const ModelsSidebar: React.FC<ModelsSidebarProps> = ({
  projects,
  onProjectSelect,
  onExampleSelect,
  onProjectDelete,
  onProjectRename,
  onClose,
  isMobile = false,
  currentProjectId,
  loadingProjectId,
  openTabIds = [],
}) => {
  const { getIcon } = useThemedIcon();
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [hoveredEllipsisId, setHoveredEllipsisId] = useState<string | null>(
    null,
  );
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    bottom?: number;
  } | null>(null);
  const moreButtonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>(
    {},
  );

  const dropdownRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingProjectId, setDeletingProjectId] = useState<string | null>(
    null,
  );
  const [deletingProjectName, setDeletingProjectName] = useState<string>("");
  const [isDeleting, setIsDeleting] = useState(false);

  const inputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  const parametricIcon = getIcon(parametricIconLight, parametricIconDark);
  const dockToLeftIcon = getIcon(dockToLeftIconLight, dockToLeftIconDark);
  const ellipsisIcon = getIcon(ellipsisIconLight, ellipsisIconDark);
  const ellipsisActiveIcon = getIcon(
    ellipsisActiveIconLight,
    ellipsisActiveIconDark,
  );
  const renameIcon = getIcon(renameIconLight, renameIconDark);
  const loadingIcon = getIcon(loadingIconLight, loadingIconDark);
  const searchIcon = getIcon(searchIconLight, searchIconDark);
  const closeIcon = getIcon(closeIconLight, closeIconDark);
  const chevronIcon = getIcon(chevronIconLight, chevronIconDark);
  const chevronActiveIcon = getIcon(
    chevronActiveIconLight,
    chevronActiveIconDark,
  );

  // Separate user projects and example projects
  const userProjects = projects.filter(
    (p) => !EXAMPLE_PROJECT_IDS.includes(p.id) && !p.copiedFromExample,
  );
  const exampleProjects = projects.filter((p) =>
    EXAMPLE_PROJECT_IDS.includes(p.id),
  );

  const [activeTab, setActiveTab] = useState<"models" | "examples">("models");

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!openDropdown) return;
      const target = event.target as Element;
      const dropdown = document.querySelector(
        `[data-sidebar-dropdown-id="${openDropdown}"]`,
      );
      const moreButton = target.closest(`.${styles.moreButton}`);
      if (moreButton) return;
      if (dropdown && !dropdown.contains(target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openDropdown]);

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleMoreClick = (e: React.MouseEvent, projectId: string) => {
    e.stopPropagation();

    if (openDropdown === projectId) {
      setOpenDropdown(null);
      setDropdownPosition(null);
      return;
    }

    const button = e.currentTarget as HTMLButtonElement;
    const rect = button.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const dropdownHeight = 120; // Approximate height

    if (spaceBelow < dropdownHeight) {
      // Open above
      setDropdownPosition({
        top: rect.top - dropdownHeight - 4,
        left: rect.right - 128, // 8rem = 128px
      });
    } else {
      // Open below
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.right - 128,
      });
    }

    setOpenDropdown(projectId);
  };

  useEffect(() => {
    if (!openDropdown) return;

    const updatePosition = () => {
      const button = moreButtonRefs.current[openDropdown];
      if (!button) return;

      const rect = button.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const dropdownHeight = 120;

      if (spaceBelow < dropdownHeight) {
        setDropdownPosition({
          top: rect.top - dropdownHeight - 4,
          left: rect.right - 128,
        });
      } else {
        setDropdownPosition({
          top: rect.bottom + 4,
          left: rect.right - 128,
        });
      }
    };

    const scrollContainer = document.querySelector(`.${styles.projectList}`);
    scrollContainer?.addEventListener("scroll", updatePosition);
    window.addEventListener("resize", updatePosition);

    return () => {
      scrollContainer?.removeEventListener("scroll", updatePosition);
      window.removeEventListener("resize", updatePosition);
    };
  }, [openDropdown]);

  const handleRenameClick = (e: React.MouseEvent, projectId: string) => {
    e.stopPropagation();
    setEditingProjectId(projectId);
    setOpenDropdown(null);
  };

  const handleDeleteClick = (
    e: React.MouseEvent,
    projectId: string,
    projectName: string,
  ) => {
    e.stopPropagation();
    setOpenDropdown(null);
    setDeletingProjectId(projectId);
    setDeletingProjectName(projectName);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingProjectId) return;

    setIsDeleting(true);
    try {
      await onProjectDelete(deletingProjectId);
      setShowDeleteModal(false);
      setDeletingProjectId(null);
      setDeletingProjectName("");
    } catch (error) {
      // console.error("Error deleting project:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCloseDeleteModal = () => {
    if (isDeleting) return;
    setShowDeleteModal(false);
    setDeletingProjectId(null);
    setDeletingProjectName("");
  };

  const handleNameKeyDown = (
    e: React.KeyboardEvent,
    projectId: string,
    currentName: string,
  ) => {
    if (e.key === "Enter") {
      const input = inputRefs.current[projectId];
      if (input) {
        const newName = input.value.trim();
        if (newName && newName !== currentName) {
          onProjectRename(projectId, newName);
        }
      }
      setEditingProjectId(null);
    } else if (e.key === "Escape") {
      setEditingProjectId(null);
    }
  };

  const handleNameBlur = (projectId: string, currentName: string) => {
    const input = inputRefs.current[projectId];
    if (input) {
      const newName = input.value.trim();
      if (newName && newName !== currentName) {
        onProjectRename(projectId, newName);
      }
    }
    setEditingProjectId(null);
  };

  const displayedProjects =
    activeTab === "models"
      ? userProjects.filter((project) =>
          project.name.toLowerCase().includes(searchQuery.toLowerCase()),
        )
      : exampleProjects;

  return (
    <>
      <div
        className={`${styles.sidebar} ${openTabIds.length === 0 ? styles.fullWidth : ""}`}
      >
        <div className={styles.content}>
          <div className={styles.sidebarTitle}>Models</div>

          {userProjects.length > 0 && (
            <div className={styles.searchContainer}>
              <img
                src={searchIcon}
                alt="Search"
                className={styles.searchIcon}
              />
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={styles.searchInput}
              />
            </div>
          )}

          {/* Tab Buttons */}
          <div className={styles.tabContainer}>
            <button
              className={`${styles.tabButton} ${activeTab === "models" ? styles.active : ""}`}
              onClick={() => setActiveTab("models")}
            >
              My models
            </button>
            <button
              className={`${styles.tabButton} ${activeTab === "examples" ? styles.active : ""}`}
              onClick={() => setActiveTab("examples")}
            >
              Examples
            </button>
          </div>

          {displayedProjects.length === 0 ? (
            <div className={styles.emptyState}>
              {activeTab === "models" && searchQuery
                ? "No models found"
                : activeTab === "models"
                  ? "No models yet"
                  : "No examples available"}
            </div>
          ) : (
            <div className={styles.projectList}>
              {displayedProjects.map((project) => {
                const isEditing = editingProjectId === project.id;
                const isDropdownOpen = openDropdown === project.id;
                const isLoading = loadingProjectId === project.id;
                const isEllipsisHovered = hoveredEllipsisId === project.id;
                const isOpenInTab = openTabIds.includes(project.id);
                const isAnyLoading = loadingProjectId !== null;
                const isCurrentProject = currentProjectId === project.id;
                const isDisabled =
                  isOpenInTab ||
                  (isAnyLoading && !isLoading) ||
                  isCurrentProject;
                const isExample = activeTab === "examples";

                return (
                  <div
                    key={project.id}
                    className={`${styles.projectItem} ${
                      currentProjectId === project.id ? styles.active : ""
                    } ${isEditing ? styles.editing : ""} ${
                      isDropdownOpen ? styles.dropdownOpen : ""
                    } ${isLoading ? styles.loading : ""} ${
                      isDisabled ? styles.disabled : ""
                    }`}
                    onClick={() => {
                      if (!isEditing && !isLoading && !isDisabled) {
                        if (isExample) {
                          onExampleSelect(project.id);
                        } else {
                          onProjectSelect(project.id);
                        }
                      }
                    }}
                    style={{
                      cursor: isDisabled ? "not-allowed" : undefined,
                    }}
                  >
                    <div className={styles.projectThumbnail}>
                      {project.thumbnail ? (
                        <img
                          src={project.thumbnail}
                          alt={project.name}
                          className={styles.thumbnailImage}
                        />
                      ) : (
                        <div className={styles.placeholderThumbnail}>
                          <img
                            src={parametricIcon}
                            alt="No preview"
                            className={styles.placeholderIcon}
                          />
                        </div>
                      )}
                    </div>
                    <div className={styles.projectInfo}>
                      <div className={styles.projectNameRow}>
                        {isEditing ? (
                          <input
                            ref={(el) => {
                              if (el && !inputRefs.current[project.id]) {
                                inputRefs.current[project.id] = el;
                                el.focus();
                                el.select();
                              } else if (el) {
                                inputRefs.current[project.id] = el;
                              }
                            }}
                            type="text"
                            defaultValue={project.name}
                            onKeyDown={(e) =>
                              handleNameKeyDown(e, project.id, project.name)
                            }
                            onBlur={() =>
                              handleNameBlur(project.id, project.name)
                            }
                            className={styles.projectNameInput}
                            onClick={(e) => e.stopPropagation()}
                          />
                        ) : (
                          <div className={styles.projectName}>
                            {project.name}
                          </div>
                        )}
                      </div>

                      {!isExample && (
                        <div className={styles.projectDate}>
                          {formatDate(new Date(project.createdAt))}
                        </div>
                      )}
                    </div>
                    {!isEditing && !isLoading && !isDisabled && !isExample && (
                      <div className={styles.moreButtonContainer}>
                        <button
                          ref={(el) =>
                            (moreButtonRefs.current[project.id] = el)
                          }
                          className={styles.moreButton}
                          onClick={(e) => handleMoreClick(e, project.id)}
                          onMouseEnter={() => setHoveredEllipsisId(project.id)}
                          onMouseLeave={() => setHoveredEllipsisId(null)}
                        >
                          <img
                            src={
                              isEllipsisHovered || isDropdownOpen
                                ? ellipsisActiveIcon
                                : ellipsisIcon
                            }
                            alt="More"
                            className={styles.ellipsisIcon}
                          />
                        </button>
                        {isDropdownOpen &&
                          dropdownPosition &&
                          createPortal(
                            <div
                              className={styles.dropdownPortal}
                              style={{
                                position: "fixed",
                                top: `${dropdownPosition.top}px`,
                                left: `${dropdownPosition.left}px`,
                              }}
                              data-sidebar-dropdown-id={project.id}
                            >
                              <div
                                className={styles.dropdownOption}
                                onClick={(e) =>
                                  handleRenameClick(e, project.id)
                                }
                              >
                                <img
                                  src={renameIcon}
                                  alt="Rename"
                                  className={styles.dropdownIcon}
                                />
                                <span className={styles.dropdownText}>
                                  Rename
                                </span>
                              </div>
                              <div className={styles.dropdownSeparator}></div>
                              <div
                                className={`${styles.dropdownOption} ${styles.deleteOption}`}
                                onClick={(e) =>
                                  handleDeleteClick(e, project.id, project.name)
                                }
                              >
                                <img
                                  src={deleteIcon}
                                  alt="Delete"
                                  className={styles.dropdownIconDelete}
                                />
                                <span className={styles.dropdownTextDelete}>
                                  Delete
                                </span>
                              </div>
                            </div>,
                            document.body,
                          )}
                      </div>
                    )}
                    {(isLoading || isDisabled) && (
                      <div className={styles.loadingIndicator}>
                        {isLoading && (
                          <img
                            src={loadingIcon}
                            alt="Loading"
                            className={styles.loadingIcon}
                          />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal &&
        createPortal(
          <div className={styles.modalOverlay} onClick={handleCloseDeleteModal}>
            <div
              className={styles.deleteModalContent}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.deleteModalHeader}>
                <h3 className={styles.deleteModalTitle}>
                  Delete model permanently?
                </h3>
                <button
                  className={styles.deleteModalCloseButton}
                  onClick={handleCloseDeleteModal}
                  aria-label="Close"
                  disabled={isDeleting}
                >
                  <img
                    src={closeIcon}
                    alt="Close"
                    className={styles.deleteModalCloseIcon}
                  />
                </button>
              </div>

              <div className={styles.deleteModalBody}>
                <div className={styles.deletePrompt}>
                  You're about to delete:
                </div>

                <div className={styles.deleteModelName}>
                  <img
                    src={parametricIcon}
                    alt="Model"
                    className={styles.deleteModelIcon}
                  />
                  <span className={styles.deleteModelText}>
                    {deletingProjectName}
                  </span>
                </div>

                <div className={styles.deleteWarningSection}>
                  <div className={styles.deleteWarningTitle}>
                    This action will permanently remove the model and all
                    associated data. This cannot be undone.
                  </div>
                </div>

                <button
                  className={styles.deleteSubmitButton}
                  onClick={handleConfirmDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? "Deleting..." : "Delete permanently"}
                </button>

                <div className={styles.deleteDisclaimer}>
                  Deleting this model will also permanently remove access for
                  all users you've previously shared it with.
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default ModelsSidebar;
